import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import appConfig from 'appConfig';
import { loadSession } from 'data/CurrentUser/sessionManager';

import {
  MenuList,
  MenuListItem,
  SVGIcon,
  HOCs,
} from '@pearsontechnology/aero-components';

import { dispatch } from 'pluginServices/store';
import { pushToast, incrementDownloadErrorReportsCount, decrementDownloadErrorReportsCount, updateToast, removeToast } from 'containers/ToastManager/actions';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import BaseMoreButton from 'components/MoreButton';
import publicationService from 'services/PublicationService';
import exportService from 'services/ExportService';
import actionMessages from "services/ExportService/messages";
import { featureKeys } from 'services/FeatureFlagService';
import { sortByDate } from 'Workspace/Publications/utils';
import { actionType, status, publishFormat, svgIconType, SB_MEDIA_GRID_FILE_SUFFIX } from '../../utils/constants';
import { Menu, MenuListItemText, Spacer, MenuListItemWrapper } from './styles';
import messages from './messages';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';
import { pushPreflightToast } from '../../../../utils/preFlightToast';
import { CSVFetchExportLink } from '../../../Cohorts/components/CohortDeploymentsList/CSVFetchExportLink';
import { fetchSBMediaGridData } from './utils/fetchSBMediaGridData';
import { withToastDelay, downloadStream, triggerDownload } from './utils/downloadHelpers';
import { generateLearnspaceUrl } from './utils/generateLearnspaceUrl';

const { enhanceWithPopper } = HOCs;
const MoreButtonWithPopper = enhanceWithPopper(BaseMoreButton);
const actionsList = [
  {
    type: actionType.CHECK_REPORT,
    icon: svgIconType.FILE_CHECK,
    message: messages.checkReport,
  },
  {
    type: actionType.GENERATE_URNS,
    icon: svgIconType.FILE_CHECK,
    message: messages.generateURNs,
  },
  {
    type: actionType.VERSION_CHANGES,
    icon: svgIconType.CONTENT_COPY,
    message: messages.versionChanges,
  },
  {
    type: actionType.DOWNLOAD_SB_MEDIA_GRID,
    icon: svgIconType.FILE_CHECK,
    message: messages.downloadSBMediaGrid,
  },
  {
    type: actionType.LEARNSPACE_URL,
    icon: svgIconType.FILE_CHECK,
    message: messages.learnspaceUrl,
  },
];
const newFlowActionsList = [
  {
    type: actionType.OUTPUT_INFO,
    icon: svgIconType.INFO_ICON,
    message: messages.outputInfo,
  },
  ...actionsList,
];

const PublicationListItemActions = ({ publicationItem, outputInfoActionCallback, getFeatureFlagValue, vesionChangesCallback, disabled = false }) => {
  const { activityId, outputType, title, market } = publicationItem;
  const totalExportSnippets = useRef(0);
  const snippet = useRef(0);
  const isPreflightPercentage = getFeatureFlagValue(featureKeys.BRNT_18784_WORKSPACE_ERROR_REPORT_PERCENTAGE_LOADER);
  const isGenerateURNSEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_PUBLICATION_GENERATE_URNS);
  const isVersionComparisonEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_PUBLICATIONS_VERSION_COMPARISON);

  const [activelyDownloadingReport, setActivelyDownlaodingReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  if (!actionsList.length) {
    return <Spacer />;
  }

  const isPublished = (publicationItem) => {
    if (publicationItem?.history?.length > 0) {
      return true;
    }
    return false;
  };

  const getPublicationReport = async () => {
    exportService.exportForReport(activityId, title, receiveOnExportComplete, outputType);
  };

  const retrievePreSignedURL = async () => {
    // Only P+ Test flow - not market needed here.
    const { publications: publicationHistory } = await publicationService.getPublicationHistory(activityId, outputType);
    if (publicationHistory) {
      const sortedPublicationHistory = sortByDate(publicationHistory);
      for (let publicationItemIndex = 0; publicationItemIndex < sortedPublicationHistory.length; publicationItemIndex++) {
        let latestPublicationItem = sortedPublicationHistory[publicationItemIndex];
        if (latestPublicationItem?.publicationJobStatus &&
          latestPublicationItem.publicationJobStatus === status.COMPLETED) {
          return exportService.getPreSignedURL(latestPublicationItem.jobId);
        }
      }
    }
  };
  const unsubscribeExportAndResetSnippets = (exportId) => {
    exportService.unSubscribeToExport(exportId);
    snippet.current = 0;
    totalExportSnippets.current = 0;
    setActivelyDownlaodingReport(null);
  };

  const requestPreflightReport = async () => {
    const { exportElementsCount, exportId } = await exportService.requestPreflightReport(activityId, outputType);
    if (exportId) {
      setActivelyDownlaodingReport(activityId);
      pushPreflightToast(actionMessages.generatingReport, title, activityId, exportId, unsubscribeExportAndResetSnippets);
      totalExportSnippets.current = exportElementsCount;
      exportService.subscribeToExport(exportId, receiveOnExportComplete);
    }
  };

  const onCompleteDownloadReport = () => decrementDownloadErrorReportsCount();

  const receiveOnExportComplete = ({ response }) => {

    const { exportId, progress } = response;
    if (isPreflightPercentage) {
      snippet.current = snippet.current + 1;
      const percentage = Math.floor((snippet.current / totalExportSnippets.current || 0) * 100);
      const toast = {
        toastId: activityId,
        percentage: percentage,
      };
      dispatch(updateToast(toast));
    }

    if (status[progress] === status.COMPLETED) {
      if (response.ambrosiaUrl) {
        unsubscribeExportAndResetSnippets(exportId);
        publicationService.checkIfReportGenerated({ activityId, exportId, title }, onCompleteDownloadReport);
      }
    }
    if (status[progress] === status.FAILED) {
      decrementDownloadErrorReportsCount();
      unsubscribeExportAndResetSnippets(exportId);
      dispatch(pushToast({ message: intl.formatMessage(messages.reportFailed) }));
    }
  };

  const handleGenerateURNS = async () => {
    const nonce = await loadSession();

    let fullURL;
    if (outputType === publishFormat.PEARSON_PLUS) {
      const { publications } = await publicationService.getPublicationHistory(activityId, outputType);
      if (!publications?.length) {
        dispatch(pushToast({ message: intl.formatMessage(messages.errorDownloadingURNS) + ' - No publication history found' }));
        return;
      }
      const sortedPublications = sortByDate(publications);
      const latestPublication = sortedPublications[0];
      fullURL = `${appConfig.getBvURNGeneratorURL()}${latestPublication.publicationId}`;
    } else {
      fullURL = `${appConfig.getEpubURNGeneratorURL()}${activityId}`;
    }

    await withToastDelay(
      async (isCancelled) => {
        const response = await fetch(fullURL, {
          method: 'GET',
          headers: { Authorization: `Bearer ${nonce}` },
        });

        if (response.status === 403) {
          throw new Error(intl.formatMessage(messages.errorDownloadingURNSNotOwner));
        }

        if (response.status < 200 || response.status > 299) {
          throw new Error(`Error ${response.status}`);
        }

        const blob = await downloadStream(response, isCancelled);
        if (!blob) return;

        const fileName = `${publicationItem?.title}-URNs.zip`;
        triggerDownload(blob, fileName);
      },
      {
        delay: 500,
        toastConfig: {
          show: (onCancel) => {
            pushPreflightToast(
              messages.generatingURNsFile,
              title,
              activityId,
              0,
              onCancel
            );
          },
          remove: () => {
            dispatch(removeToast({ toastId: activityId }));
          },
        },
      }
    ).catch((error) => {
      dispatch(
        pushToast({
          message: `${intl.formatMessage(messages.errorDownloadingURNS)} - ${error.message || error
          }`,
        })
      );
    });
  };

  const handleLearnspaceUrl = async () => {
    const learnspaceUrl = await generateLearnspaceUrl(publicationItem);
    window.open(learnspaceUrl, '_blank');
  };


  const onClickItem = (type) => async () => {
    const { CHECK_REPORT, OUTPUT_INFO, PRE_SIGNED_URL, GENERATE_URNS, VERSION_CHANGES, LEARNSPACE_URL } = actionType;
    if (type === CHECK_REPORT) {
      dispatch(incrementDownloadErrorReportsCount());
      if (isPreflightPercentage) {
        requestPreflightReport();
      } else {
        getPublicationReport();
      }
    } else if (type === OUTPUT_INFO) {
      outputInfoActionCallback(activityId, outputType); // , market ? TO DO ? only CLASS ON DEMAND and PUBLISHED
    } else if (type === PRE_SIGNED_URL) {
      const url = await retrievePreSignedURL();
      prompt(intl.formatMessage(messages.copyPreSignedURLToClipboard), url);
    } else if (type === GENERATE_URNS) {
      handleGenerateURNS();
    } else if (type === VERSION_CHANGES) {
      vesionChangesCallback(publicationItem);
    } else if (type === LEARNSPACE_URL) {
      await handleLearnspaceUrl();
    }
  };

  const getActionsList = () => {
    let actionsListToUse = [...actionsList];
    let newFlowActionsListToUse = [...newFlowActionsList];
    const isGenerateURNSOutputType = outputType === publishFormat.EPUB || outputType === publishFormat.PEARSON_PLUS;
    const isLearnspaceUrlEnabled = outputType === publishFormat.PEARSON_PLUS;
    if (!isGenerateURNSEnabled || !isGenerateURNSOutputType) {
      newFlowActionsListToUse = newFlowActionsListToUse.filter((item) => item.type != actionType.GENERATE_URNS);
      actionsListToUse = actionsListToUse.filter((item) => item.type != actionType.GENERATE_URNS);
    }

    if (!isLearnspaceUrlEnabled) {
      newFlowActionsListToUse = newFlowActionsListToUse.filter((item) => item.type != actionType.LEARNSPACE_URL);
      actionsListToUse = actionsListToUse.filter((item) => item.type != actionType.LEARNSPACE_URL);
    }

    if (!isVersionComparisonEnabled) {
      newFlowActionsListToUse = newFlowActionsListToUse.filter((item) => item.type != actionType.VERSION_CHANGES);
      actionsListToUse = actionsListToUse.filter((item) => item.type != actionType.VERSION_CHANGES);
    }

    if (outputType === publishFormat.CLASS_ON_DEMAND && isPublished(publicationItem)) {
      return newFlowActionsListToUse;
    } else if (outputType === publishFormat.PEARSON_PLUS_TEST) {
      return [...actionsListToUse, {
        type: actionType.PRE_SIGNED_URL,
        icon: svgIconType.COPY_CLIPBOARD,
        message: messages.generatePreSignedURL,
      }];
    } else {
      return actionsListToUse;
    }
  };

  const checkifReportDownloading = (type) => type === actionType.CHECK_REPORT && activityId && activityId === activelyDownloadingReport;
  const renderActionsMenu = (actionsList) => (
    actionsList.map((item) => (
      <MenuListItemWrapper key={item.type} activelyDownloadingReport={activelyDownloadingReport === publicationItem.activityId}>
        {item.type === actionType.DOWNLOAD_SB_MEDIA_GRID ? (
          <MenuListItem key={item.type} >
            <SVGIcon icon={item.icon} />
            <CSVFetchExportLink
              fileName={`${title}-${SB_MEDIA_GRID_FILE_SUFFIX}`}
              fetchCSVData={() => fetchSBMediaGridData(activityId, outputType, market)}
              activityId={activityId}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              title={title}
            >
              <MenuListItemText>
                {intl.formatMessage(item.message)}
              </MenuListItemText>
            </CSVFetchExportLink>
          </MenuListItem>
        ) : (
          <MenuListItem key={item.type} disabled={checkifReportDownloading(item.type)} onClick={onClickItem(item.type)} data-type={`${item.type}`}>
            <SVGIcon icon={item.icon} />
            <MenuListItemText>
              {intl.formatMessage(item.message)}
            </MenuListItemText>
          </MenuListItem>
        )}
      </MenuListItemWrapper>
    ))
  );

  return (
    <MoreButtonWithPopper disabled={disabled} placement="bottom-end" hidepopperonclick>
      <Menu>
        <MenuList>
          {renderActionsMenu(getActionsList())}
        </MenuList>
      </Menu>
    </MoreButtonWithPopper>
  );
};

PublicationListItemActions.propTypes = {
  publicationItem: PropTypes.object,
  outputInfoActionCallback: PropTypes.func,
  vesionChangesCallback: PropTypes.func,
  disabled: PropTypes.bool,
};

const flagNames = [featureKeys.BRNT_18784_WORKSPACE_ERROR_REPORT_PERCENTAGE_LOADER];
export default enhanceWithFeatureFlagSubscription(PublicationListItemActions, flagNames);
